import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import FaqSchema from './FaqSchema';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import InterviewPage from './InterviewPage';
import './InterviewAssistantLanding.css';

const HERO_IMG = '/images/interview-assistant/hero-ai-listener.png';
const TRANSCRIPT_IMG = '/images/interview-assistant/live-transcription.png';
const SUGGESTIONS_IMG = '/images/interview-assistant/realtime-suggestions.png';
const SECURE_IMG = '/images/interview-assistant/private-secure.png';

/* ---------- Three.js neural orb hero canvas ---------- */
const NeuralListenerCanvas = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 70;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // Audio-wave ring particles
    const ringCount = 240;
    const ringPositions = new Float32Array(ringCount * 3);
    const ringPhases = new Float32Array(ringCount);
    for (let i = 0; i < ringCount; i++) {
      const a = (i / ringCount) * Math.PI * 2;
      ringPositions[i * 3] = Math.cos(a) * 22;
      ringPositions[i * 3 + 1] = Math.sin(a) * 22;
      ringPositions[i * 3 + 2] = 0;
      ringPhases[i] = a;
    }
    const ringGeo = new THREE.BufferGeometry();
    ringGeo.setAttribute('position', new THREE.BufferAttribute(ringPositions, 3));

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.3, 'rgba(0,255,255,0.9)');
      g.addColorStop(0.7, 'rgba(224,163,255,0.3)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    const ringMat = new THREE.PointsMaterial({
      size: 1.4,
      map: sprite,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
    });
    const ring = new THREE.Points(ringGeo, ringMat);
    scene.add(ring);

    // ambient particle field
    const fieldCount = 220;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 160;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 100;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.12;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 1.2,
      map: sprite,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
      opacity: 0.6,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    // central listening orb
    const orbGeo = new THREE.IcosahedronGeometry(10, 1);
    const orbMat = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      wireframe: true,
      transparent: true,
      opacity: 0.4,
    });
    const orb = new THREE.Mesh(orbGeo, orbMat);
    scene.add(orb);

    const orb2 = new THREE.Mesh(
      new THREE.IcosahedronGeometry(13, 1),
      new THREE.MeshBasicMaterial({
        color: 0xe0a3ff,
        wireframe: true,
        transparent: true,
        opacity: 0.22,
      }),
    );
    scene.add(orb2);

    let raf;
    let mx = 0;
    let my = 0;
    let t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    const onResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.02;

      // ring breathing — simulates audio waveform
      const rPos = ringGeo.attributes.position.array;
      for (let i = 0; i < ringCount; i++) {
        const a = ringPhases[i];
        const r = 22 + Math.sin(a * 6 + t) * 1.6 + Math.sin(t * 2) * 0.6;
        rPos[i * 3] = Math.cos(a) * r;
        rPos[i * 3 + 1] = Math.sin(a) * r;
      }
      ringGeo.attributes.position.needsUpdate = true;
      ring.rotation.z += 0.002;

      // field drift
      const fPos = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fPos[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fPos[i * 3 + 1] > 50) fPos[i * 3 + 1] = -50;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      orb.rotation.x += 0.004;
      orb.rotation.y += 0.005;
      orb2.rotation.x -= 0.002;
      orb2.rotation.y -= 0.003;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      ringGeo.dispose();
      ringMat.dispose();
      fieldGeo.dispose();
      fieldMat.dispose();
      orbGeo.dispose();
      orbMat.dispose();
      orb2.geometry.dispose();
      orb2.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="ial-hero-canvas" aria-hidden="true" />;
};

/* ---------- Live transcription typewriter demo ---------- */
const TRANSCRIPT_LINES = [
  { who: 'Interviewer', text: 'Walk me through how you handle a system going down in production.' },
  { who: 'AI', text: 'Suggested STAR: incident at Company X, p95 latency tripled, you led mitigation in 14 min.' },
  { who: 'Candidate', text: 'Sure — last quarter at Acme our checkout p95 jumped from 120ms to 380ms…' },
];

const LiveTranscriptDemo = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting && active === -1) setActive(0);
      }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [active]);

  useEffect(() => {
    if (active < 0 || active >= TRANSCRIPT_LINES.length) return;
    const target = TRANSCRIPT_LINES[active].text;
    let i = 0;
    setTyped('');
    const id = setInterval(() => {
      i += 1;
      setTyped(target.slice(0, i));
      if (i >= target.length) {
        clearInterval(id);
        setTimeout(() => setActive((a) => a + 1), 700);
      }
    }, 20);
    return () => clearInterval(id);
  }, [active]);

  return (
    <section className="ial-section" ref={ref}>
      <div className="ial-section-grid">
        <div className="ial-section-text">
          <span className="ial-eyebrow">LIVE TRANSCRIPTION</span>
          <h2 className="ial-h2">
            We hear every word — <span className="ial-grad-text">before you even respond</span>.
          </h2>
          <p className="ial-lead">
            The assistant transcribes the interviewer in real time and detects what's being asked.
            By the time you start speaking, you already have a structured answer ready to follow.
          </p>
          <ul className="ial-bullet-list">
            <li><span className="ial-dot" />Continuous low-latency speech-to-text</li>
            <li><span className="ial-dot" />Speaker diarization (interviewer vs you)</li>
            <li><span className="ial-dot" />Question-type detection (behavioral / system / coding)</li>
          </ul>
        </div>

        <div className="ial-transcript-card">
          {TRANSCRIPT_LINES.map((line, i) => {
            const isActive = i === active;
            const isDone = i < active;
            const isPending = i > active;
            return (
              <div
                key={i}
                className={`ial-tr-line ${line.who === 'AI' ? 'ai' : line.who === 'Candidate' ? 'me' : 'you'} ${isActive ? 'live' : ''} ${isPending ? 'pending' : ''}`}
              >
                <span className="ial-tr-tag">{line.who}</span>
                <span className="ial-tr-text">
                  {isDone ? line.text : isActive ? typed : ''}
                  {isActive && active < TRANSCRIPT_LINES.length && <span className="ial-caret">|</span>}
                </span>
              </div>
            );
          })}
          {active >= TRANSCRIPT_LINES.length && (
            <button type="button" className="ial-tr-replay" onClick={() => setActive(0)}>↻ Replay demo</button>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- Suggestions panel demo ---------- */
const SUGGESTIONS = [
  { tag: 'STAR', text: 'Lead with the situation — Acme checkout p95 jumped 3× during peak shopping' },
  { tag: 'METRIC', text: 'Cut latency 38% in 14 minutes by sharding the cart-summary cache' },
  { tag: 'IMPACT', text: 'Saved $42K in lost revenue and unblocked 3 dependent product teams' },
  { tag: 'LEARN', text: 'Now run quarterly cache-key audits — caught 2 similar regressions early' },
];

const SuggestionsDemo = () => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting && revealed === 0) setRevealed(1);
      }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealed]);

  useEffect(() => {
    if (revealed === 0 || revealed >= SUGGESTIONS.length) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), 750);
    return () => clearTimeout(t);
  }, [revealed]);

  return (
    <section className="ial-section" ref={ref}>
      <div className="ial-section-grid ial-flip">
        <div className="ial-suggest-card">
          <div className="ial-suggest-glow" />
          <div className="ial-suggest-header">
            <div className="ial-suggest-pulse" />
            <span>AI is composing answer…</span>
          </div>
          <div className="ial-suggest-body">
            {SUGGESTIONS.map((s, i) => (
              <div
                key={i}
                className={`ial-suggest-row ${i < revealed ? 'lit' : ''}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <span className="ial-suggest-tag">{s.tag}</span>
                <span className="ial-suggest-text">{s.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ial-section-text">
          <span className="ial-eyebrow">REAL-TIME ANSWERS</span>
          <h2 className="ial-h2">
            STAR-formatted answers, <span className="ial-grad-text">composed as the question lands</span>.
          </h2>
          <p className="ial-lead">
            The assistant reads the question, reaches into your resume + the JD, and surfaces a
            structured answer — Situation, Task, Action, Result — with metric placeholders pulled
            from your real experience. You read your way, in your voice.
          </p>
          <ul className="ial-bullet-list">
            <li><span className="ial-dot" />Pulls from your resume so answers stay truthful</li>
            <li><span className="ial-dot" />Tailors tone to the role's seniority</li>
            <li><span className="ial-dot" />Keeps pace with multi-part follow-ups</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Set the stage', body: 'Paste the JD, upload your resume, share the interview window. 30 seconds.' },
  { title: 'AI listens', body: 'Real-time transcription + speaker detection — interviewer vs you.' },
  { title: 'Question parsed', body: 'STAR / system / behavioral / coding — type detected automatically.' },
  { title: 'Answer composed', body: 'Structured response built from your resume + the role context.' },
  { title: 'You deliver', body: 'Read it your way — natural pace, your voice, your story.' },
  { title: 'Follow-ups handled', body: 'Mid-answer pivots? AI tracks the thread and adjusts in real time.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="ial-section" ref={ref}>
      <div className="ial-section-head">
        <span className="ial-eyebrow">HOW IT WORKS</span>
        <h2 className="ial-h2">
          Six steps. <span className="ial-grad-text">Zero pressure.</span>
        </h2>
        <p className="ial-lead ial-center">
          Every step lights up as it happens. The whole loop runs in under a second.
        </p>
      </div>

      <div className="ial-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`ial-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="ial-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`ial-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 92, suffix: '%', label: 'Answer-confidence boost' },
  { val: 1, suffix: 's', label: 'Avg. suggestion latency' },
  { val: 250, suffix: 'K+', label: 'Sessions assisted' },
  { val: 4.9, suffix: '/5', label: 'User rating' },
];

const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};

const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="ial-stat">
      <div className="ial-stat-num">{v}{stat.suffix}</div>
      <div className="ial-stat-lbl">{stat.label}</div>
    </div>
  );
};

const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setStart(true); });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="ial-stats-band" ref={ref}>
      <div className="ial-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'Does the interviewer see the assistant?', a: 'No. The assistant runs locally in your browser/window and only you see its panel. Screen sharing is one-way — to your AI, not to the interviewer.' },
  { q: 'Will I sound robotic if I read the suggestions?', a: 'You won\'t — the suggestions are scaffolds (STAR / METRIC / IMPACT / LEARN) that you fill out in your own words. We never script word-for-word answers.' },
  { q: 'Is my interview audio stored?', a: 'No. Audio is transcribed in-stream. We don\'t persist your live audio after the session ends. Transcripts are kept only if you explicitly save them.' },
  { q: 'What about coding questions?', a: 'For coding rounds we surface algorithm hints and complexity tradeoffs, not literal solutions. The goal is to keep you unblocked, not to do the interview for you.' },
  { q: 'Does it support video / Zoom / Teams?', a: 'Yes — anything you can share to a window. Zoom, Google Meet, Teams, Hirevue, CodeSignal all work.' },
  { q: 'How fast is the assistant?', a: 'Sub-second. Suggestions render before you typically finish processing the question yourself.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="ial-section">
      <div className="ial-section-head">
        <span className="ial-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="ial-h2">Everything you wanted to ask</h2>
      </div>
      <div className="ial-faq-list">
        {FAQS.map((f, i) => (
          <button
            key={f.q}
            type="button"
            className={`ial-faq-item ${open === i ? 'open' : ''}`}
            onClick={() => setOpen(open === i ? -1 : i)}
          >
            <div className="ial-faq-q">
              <span>{f.q}</span>
              <span className="ial-faq-chev">+</span>
            </div>
            <div className="ial-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Page root ---------- */
const InterviewAssistantLanding = () => {
  const navigate = useNavigate();
  const studioRef = useRef(null);
  const goSession = useCallback(() => {
    if (studioRef.current) {
      studioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => {
    if (show) navigate('/?openLogin=1');
  }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="ial-page">
      <Seo
        title="AI Interview Assistant — Real-Time Interview Help"
        description="Get real-time AI help during live job interviews. Terview listens and tells you exactly what to say, instantly."
        path="/InterviewAssistant"
      />
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="ial-hero" ref={heroRef}>
        <NeuralListenerCanvas />
        <div className="ial-hero-bg" />
        <img src={HERO_IMG} alt="" className="ial-hero-img" loading="eager" />

        <div className="ial-hero-content">
          <span className="ial-hero-pill">
            <span className="ial-pill-dot" /> Live · Real-time · Invisible to interviewer
          </span>
          <h1 className="ial-h1">
            Walk into every interview with a <span className="ial-grad-text">co-pilot</span> in your ear.
          </h1>
          <p className="ial-hero-sub">
            Real-time transcription, STAR-formatted answers, role-tailored hints — composed as the
            question lands. The interviewer only ever sees you. We just make sure you sound like
            your most prepared self.
          </p>

          <div className="ial-hero-cta">
            <button className="ial-btn ial-btn-primary" onClick={goSession}>
              <span>Launch interview assistant</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="ial-btn ial-btn-ghost" onClick={() => navigate('/mockInterview')}>
              Try mock interview first
            </button>
          </div>

          <div className="ial-hero-checks">
            <span>✓ Sub-second latency</span>
            <span>✓ Private — only you see it</span>
            <span>✓ Works with Zoom / Meet / Teams</span>
          </div>
        </div>

        {/* floating glass chat panel */}
        <div className="ial-floater" aria-hidden="true">
          <div className="ial-floater-card">
            <div className="ial-floater-head">
              <span className="ial-floater-dot" />
              <span>Live</span>
            </div>
            <div className="ial-floater-line ial-floater-q">"Tell me about a time you led a tough rollout."</div>
            <div className="ial-floater-tag">STAR · BEHAVIORAL</div>
            <div className="ial-floater-bar" />
            <div className="ial-floater-bar w70" />
            <div className="ial-floater-bar w85" />
            <div className="ial-floater-bar w55" />
          </div>
          <div className="ial-floater-glow" />
        </div>
      </section>

      <section className="ial-studio" ref={studioRef} id="studio">
        <div className="ial-studio-head">
          <span className="ial-eyebrow">INTERVIEW STUDIO</span>
          <h2 className="ial-h2">
            Set up your session — <span className="ial-grad-text">live, in this page</span>.
          </h2>
          <p className="ial-lead ial-center">
            Drop in your job info, link your resume, share the interview window. The assistant boots
            up here and stays with you for the entire session.
          </p>
        </div>
        <InterviewPage embedded />
      </section>

      <LiveTranscriptDemo />
      <SuggestionsDemo />

      <section className="ial-section">
        <div className="ial-section-grid">
          <div className="ial-section-text">
            <span className="ial-eyebrow">PRIVATE BY DEFAULT</span>
            <h2 className="ial-h2">
              Help that's <span className="ial-grad-text">truly invisible</span> to the interviewer.
            </h2>
            <p className="ial-lead">
              The assistant lives in your tab — never on the call. No browser extensions injected
              into your meeting, no overlays the interviewer can see, no audio routed back. What
              they see is exactly what they'd see without us.
            </p>
            <ul className="ial-bullet-list">
              <li><span className="ial-dot" />No screen overlays on the meeting itself</li>
              <li><span className="ial-dot" />Audio transcribed in-stream — not stored</li>
              <li><span className="ial-dot" />Sessions encrypted in transit and at rest</li>
            </ul>
          </div>
          <div className="ial-img-frame">
            <img src={SECURE_IMG} alt="Secure private interview assistance" />
            <div className="ial-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="ial-section">
        <div className="ial-section-grid ial-flip">
          <div className="ial-img-frame">
            <img src={TRANSCRIPT_IMG} alt="Live transcription panel" />
            <div className="ial-img-frame-glow" />
          </div>
          <div className="ial-section-text">
            <span className="ial-eyebrow">SCREEN SHARE TO AI</span>
            <h2 className="ial-h2">
              Share your interview window. <span className="ial-grad-text">Only with us.</span>
            </h2>
            <p className="ial-lead">
              Click "Share Screen" once at the start. The assistant watches the meeting tab, hears
              the question, and surfaces help inside our private panel. Nothing flows out to the
              interviewer.
            </p>
          </div>
        </div>
      </section>

      <section className="ial-section">
        <div className="ial-section-grid">
          <div className="ial-section-text">
            <span className="ial-eyebrow">RESUME-AWARE</span>
            <h2 className="ial-h2">
              Trained on <span className="ial-grad-text">your story</span>, not a script.
            </h2>
            <p className="ial-lead">
              Drop your resume once and every suggestion the assistant generates pulls from your
              actual roles, projects, and metrics. No fake numbers, no invented experience —
              just your truth, structured beautifully.
            </p>
          </div>
          <div className="ial-img-frame">
            <img src={SUGGESTIONS_IMG} alt="Real-time suggestions" />
            <div className="ial-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="ial-cta-final">
        <div className="ial-cta-card">
          <div className="ial-cta-glow" />
          <h2 className="ial-h2">
            Your next interview <span className="ial-grad-text">deserves a co-pilot</span>.
          </h2>
          <p>Stop walking in alone. Launch the assistant and ace the next one.</p>
          <div className="ial-hero-cta center">
            <button className="ial-btn ial-btn-primary" onClick={goSession}>
              <span>Launch assistant now</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="ial-btn ial-btn-ghost" onClick={() => navigate('/mockInterview')}>Practice on mock first</button>
          </div>
        </div>
      </section>

      <FaqSchema faqs={FAQS} />
      <Footer />
    </div>
  );
};

export default InterviewAssistantLanding;
